import {Text, Stack, Group, UnstyledButton} from "@mantine/core";
import {Link} from "react-router-dom";
import {useTranslation} from "react-i18next";
import IconChevronDown from "../../../../assets/chevron-down.svg?react";
import {DirectSearchResult} from "../../../../types";
import {useExperimentalLinkResolution} from "../../../../components/ExperimentalLink";
import {
    buildFileDownloadHref,
    buildFileTopicUrl,
    fileExtensionMap,
    isValidFileType,
} from "../../../content/contentUtils";
import {ResourceType} from "../../../content/types";
import {resolveTypeLabel} from "../../../content/typeIcons";
import classes from "./SearchResultsRenderer.module.css";

interface ResultRowProps {
    result: DirectSearchResult;
    index: number;
    showBorder: boolean;
    onClick?: () => void;
}

export function ResultRow({result, showBorder, onClick}: ResultRowProps) {
    const {t} = useTranslation();
    const meta = result.metadata;
    const resourceTypeId = meta?.resourceTypeid as
        | ResourceType
        | undefined;
    const topicId = meta?.topicId;
    // A PDF hit opens in the in-app viewer instead of downloading. Like the
    // download href, its route is built from the topic id rather than the url.
    const isPdfInApp =
        Boolean(topicId) &&
        String(resourceTypeId) === ResourceType.pdf;
    const isFile =
        isValidFileType(resourceTypeId) && Boolean(topicId) && !isPdfInApp;

    const {effectiveHref, internalPath} = useExperimentalLinkResolution(
        isPdfInApp ? buildFileTopicUrl(result.title, topicId!, meta) : result.url
    );
    const href = isFile ? buildFileDownloadHref(topicId!) : effectiveHref;
    const extension = isFile
        ? fileExtensionMap[String(resourceTypeId)] ?? ""
        : "";

    const badgeLabel = resolveTypeLabel(t, meta?.topicType, result.url);
    const subText = result.description || result.snippet || badgeLabel;

    const rowClassName = `${classes.resultRow} ${showBorder ? classes.resultRowBorder : ""}`;
    const rowContent = (
        <Group wrap="nowrap" gap="md" align="center">
            <Stack gap={2} style={{flex: 1, minWidth: 0}}>
                <Text size="sm" className={classes.resultTitle}>
                    {result.title}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={2}>
                    {subText}
                </Text>
            </Stack>
            <span className={classes.typeBadge}>{badgeLabel}</span>
            <IconChevronDown
                width={12}
                height={12}
                className={classes.chevronRight}
            />
        </Group>
    );

    // A dib content url opens in the app, like the source references do.
    if (!isFile && internalPath !== null) {
        return (
            <UnstyledButton
                className={rowClassName}
                onClick={onClick}
                component={Link}
                to={internalPath}
            >
                {rowContent}
            </UnstyledButton>
        );
    }

    const anchorProps = isFile
        ? {
              download: `${result.title}${extension}`,
              "data-pw": "file-download" as const,
          }
        : {
              target: "_blank" as const,
              rel: "noopener noreferrer" as const,
          };

    return (
        <UnstyledButton
            className={rowClassName}
            component="a"
            href={href}
            onClick={onClick}
            {...anchorProps}
        >
            {rowContent}
        </UnstyledButton>
    );
}
